import { useEffect, useState, Suspense, lazy, startTransition } from 'react';
import { MotionConfig } from 'framer-motion';
import { Routes, Route, Link, Navigate } from 'react-router-dom';
import Header from './components/Header';
import AppErrorBoundary from './components/AppErrorBoundary';
import ScrollToTop from './components/ScrollToTop';
import LoadingSpinner from './components/LoadingSpinner';
import { domainToolLinks, learnLinks, siteHealthLinks } from './config/navigation';

// Eager load the homepage because it provides the first render.
import HomePage from './pages/HomePage';

const PremiumDomainsPage = lazy(() => import('./pages/PremiumDomainsPage'));
const GeneratorPage = lazy(() => import('./pages/GeneratorPage'));
const BlogListPage = lazy(() => import('./pages/BlogListPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const DnsHealthPage = lazy(() => import('./pages/DnsHealthPage'));
const SslCheckerPage = lazy(() => import('./pages/SslCheckerPage'));
const ComparePage = lazy(() => import('./pages/ComparePage'));
const AuditPage = lazy(() => import('./pages/AuditPage'));
const SearchVisibilityPage = lazy(() => import('./pages/SearchVisibilityPage'));
const SearchVisibilityMethodologyPage = lazy(() => import('./pages/SearchVisibilityMethodologyPage'));
const AiCrawlerRegistryPage = lazy(() => import('./pages/AiCrawlerRegistryPage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const ScannerInfoPage = lazy(() => import('./pages/ScannerInfoPage'));
const GlossaryPage = lazy(() => import('./pages/GlossaryPage'));
const GlossaryTermPage = lazy(() => import('./pages/GlossaryTermPage'));
const TechStackHubPage = lazy(() => import('./pages/TechStackHubPage'));
const TechStackPage = lazy(() => import('./pages/TechStackPage'));
const ComparisonHubPage = lazy(() => import('./pages/ComparisonHubPage'));
const ToolComparisonPage = lazy(() => import('./pages/ToolComparisonPage'));

const PageLoader = () => (
  <div className="flex min-h-[50vh] items-center justify-center" role="status" aria-label="Loading page">
    <LoadingSpinner size="large" />
  </div>
);

const getSavedTheme = () => {
  try {
    const savedTheme = window.localStorage.getItem('theme');
    return savedTheme === 'light' || savedTheme === 'dark' ? savedTheme : null;
  } catch (error) {
    return null;
  }
};

const getSystemTheme = () => {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const getInitialTheme = () => {
  if (typeof window === 'undefined') return 'light';
  return getSavedTheme() || getSystemTheme();
};

const FooterLinkGroup = ({ title, links }) => (
  <div>
    <h2 className="text-sm font-bold uppercase tracking-[0.14em] text-navy-800 dark:text-navy-300">{title}</h2>
    <ul className="mt-4 space-y-3">
      {links.map((link) => (
        <li key={link.to}>
          <Link
            to={link.to}
            className="text-sm font-medium text-gray-600 transition-colors hover:text-navy-800 dark:text-gray-300 dark:hover:text-navy-300"
          >
            {link.title}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

const Footer = () => (
  <footer className="mt-20 border-t border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
      <div className="grid gap-10 lg:grid-cols-[1.35fr_2fr]">
        <div className="max-w-md">
          <Link to="/" className="text-2xl font-bold tracking-tight text-gray-950 dark:text-white">
            DomainOptic
          </Link>
          <p className="mt-4 text-sm leading-6 text-gray-600 dark:text-gray-300">
            Search and AI visibility checks, passive security audits, and domain research for builders. Understand the public signals your site sends and find a stronger name for what you are building.
          </p>
          <Link to="/audit/" className="ui-button-primary mt-6 inline-flex">
            Run an audit
          </Link>
        </div>

        <nav aria-label="Footer navigation" className="grid grid-cols-2 gap-8 sm:grid-cols-3">
          <FooterLinkGroup title="Site Health" links={siteHealthLinks} />
          <FooterLinkGroup title="Domains" links={domainToolLinks} />
          <FooterLinkGroup title="Learn" links={learnLinks} />
        </nav>
      </div>

      <div className="mt-12 flex flex-col gap-4 border-t border-gray-200 pt-6 text-sm text-gray-600 dark:border-gray-800 dark:text-gray-400 sm:flex-row sm:items-center sm:justify-between">
        <p>&copy; {new Date().getFullYear()} DomainOptic. Built for builders.</p>
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          <Link to="/privacy/" className="hover:text-navy-800 dark:hover:text-navy-300">Privacy</Link>
          <Link to="/terms/" className="hover:text-navy-800 dark:hover:text-navy-300">Terms</Link>
          <a href="/sitemap.xml" className="hover:text-navy-800 dark:hover:text-navy-300">Sitemap</a>
          <a href="mailto:brenbuilds@protonmail.com" className="hover:text-navy-800 dark:hover:text-navy-300">Contact</a>
        </div>
      </div>
    </div>
  </footer>
);

function App() {
  const [theme, setTheme] = useState(getInitialTheme);

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.toggle('dark', theme === 'dark');
    root.style.colorScheme = theme;
  }, [theme]);

  useEffect(() => {
    if (getSavedTheme()) return undefined;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = (event) => setTheme(event.matches ? 'dark' : 'light');
    mediaQuery.addEventListener('change', handleSystemThemeChange);
    return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
  }, []);

  const handleThemeToggle = () => {
    startTransition(() => {
      setTheme((currentTheme) => {
        const nextTheme = currentTheme === 'light' ? 'dark' : 'light';
        try {
          window.localStorage.setItem('theme', nextTheme);
        } catch (error) {
          // The visual preference still applies for this session when storage is unavailable.
        }
        return nextTheme;
      });
    });
  };

  return (
    <MotionConfig reducedMotion="user">
      <div className="flex min-h-screen flex-col bg-gray-50 text-gray-900 transition-colors duration-200 motion-reduce:transition-none dark:bg-gray-900 dark:text-gray-100">
        <a href="#main-content" className="skip-link">Skip to main content</a>
        <ScrollToTop />
        <Header currentTheme={theme} onToggleTheme={handleThemeToggle} />

        <main id="main-content" tabIndex="-1" className="flex-grow outline-none">
          <AppErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/premium" element={<PremiumDomainsPage />} />
              <Route path="/generator" element={<GeneratorPage />} />
              <Route path="/blog" element={<BlogListPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/dns-health" element={<DnsHealthPage />} />
              <Route path="/ssl-checker" element={<SslCheckerPage />} />
              <Route path="/compare" element={<ComparePage />} />
              <Route path="/audit" element={<AuditPage />} />
              <Route path="/search-visibility" element={<SearchVisibilityPage />} />
              <Route path="/search-visibility/methodology" element={<SearchVisibilityMethodologyPage />} />
              <Route path="/search-visibility/ai-crawler-registry" element={<AiCrawlerRegistryPage />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsOfServicePage />} />
              <Route path="/pricing" element={<Navigate to="/audit/" replace />} />
              <Route path="/scanner-info" element={<ScannerInfoPage />} />
              <Route path="/glossary" element={<GlossaryPage />} />
              <Route path="/glossary/:slug" element={<GlossaryTermPage />} />
              <Route path="/security" element={<TechStackHubPage />} />
              <Route path="/security/:slug" element={<TechStackPage />} />
              <Route path="/vs" element={<ComparisonHubPage />} />
              <Route path="/vs/:slug" element={<ToolComparisonPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
          </AppErrorBoundary>
        </main>

        <Footer />
      </div>
    </MotionConfig>
  );
}

export default App;
